import classnames from 'classnames';
import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { CurrencyInput, Input, InputRef } from '@components';

import { CountStatus } from '@app/features/CashBalance/CashBalanceHelper';

import { amountSignedPound, removeNegativeSymbol } from '@utils/currencyFormatter';

import { PouchSubType } from '@api/generator';

import { DenominationLadderType } from '@constants/denominations';

import { DenominationLadderTableHeader } from '../components/DenominationHeader';
import { DenominationInfo } from '../DenominationLadderTypes';
import {
  denominationLadderForPouchSubType,
  getDenominationFieldName as getFieldName,
  getMaxQuantityInBag,
  getBagLooseCoinsValue,
  getBagQuantityValue,
  updateDenominationWithValues,
} from '../DenominationLadderUtils';

import styles from './DenominationLadder.module.scss';

const languageNamespace = 'denomination-ladder';

const numericOnlyMask = (quantity?: string): string => {
  if (quantity === undefined) {
    return '';
  }
  return `${quantity}`.replace(/\D/g, '');
};

export type DenominationLadderProps = {
  denominationType: DenominationLadderType | undefined;
  existingDenominationData?: DenominationInfo[] | null;
  onChange?: (denominations: DenominationInfo[]) => void;
  onErrorChange?: (denominationId: string, hasError: boolean) => void;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  countStatus?: CountStatus;
  hideErrorCode?: boolean;
  maxLimit?: number;
};

interface QuantityFieldProps {
  denomination: DenominationInfo;
  defaultValues: FieldValues;
  fieldValues: FieldValues;
  dirtyFields: Set<string>;
  readOnly: boolean;
  isPartBag: boolean;
  handleChange: (newQuantity: string, fieldID: string, denomination: DenominationInfo) => void;
  disabled?: boolean;
  formMode: FormMode;
}

type FormMode = 'value' | 'quantity' | null;
type FieldValues = Record<string, number>;

export enum DenominationBagType {
  partBag = 'partBag',
  fullBag = 'fullBag',
  unknown = 'unknown',
}

export type DenominationLadderRef = { reset: () => void; getData: () => DenominationInfo[] };

const QuantityField = ({
  denomination,
  defaultValues,
  dirtyFields,
  fieldValues,
  readOnly,
  isPartBag,
  handleChange,
  disabled,
  formMode,
}: QuantityFieldProps) => {
  const { t } = useTranslation(languageNamespace);
  const inputRef = useRef<InputRef>(null);
  const fieldID = getFieldName(denomination, 'quantity');
  const defaultValue = defaultValues?.[fieldID] || 0;
  const actualValue = fieldValues?.[fieldID] !== undefined ? fieldValues?.[fieldID] : defaultValue;

  if (!dirtyFields.has(fieldID) && actualValue === 0 && defaultValue === 0) {
    // TODO: This is causing the skipped PouchPreparationDetails tests to fail, we shouldn't be escape-hatching out of the react
    // paradigm. Input should be refactored in the future to be a controlled component, and this can then be removed. But the change will have
    // a large surface area
    inputRef?.current?.reset();
  }

  if (readOnly) return defaultValue === 0 ? '0' : removeNegativeSymbol(defaultValue);

  const errorMessage = () => {
    const maxQuantityInBag = denomination.maxQuantityInBag ?? Infinity;
    const isExceedingMax = !isPartBag && actualValue > maxQuantityInBag;
    return isExceedingMax ? `Quantity exceeds maximum of ${denomination.maxQuantityInBag}` : '';
  };
  return (
    <Input
      ref={inputRef}
      className={styles.input}
      name={fieldID}
      testId={fieldID}
      defaultValue={defaultValue || ''}
      value={actualValue || ''}
      provideValueOnChange
      onChange={(value) => handleChange(value, fieldID, denomination)}
      placeholder={t('quantity')}
      errorMessage={errorMessage()}
      selectAllOnFocus
      disabled={disabled || denomination.unitValuePence === null || formMode === 'value'}
      maskFunc={numericOnlyMask}
    />
  );
};

const DenominationLadder = (
  {
    denominationType,
    existingDenominationData,
    onChange,
    onErrorChange,
    disabled,
    readOnly = false,
    countStatus,
    hideErrorCode = false,
    maxLimit,
  }: DenominationLadderProps,
  ref: Ref<DenominationLadderRef>,
) => {
  const { t } = useTranslation(languageNamespace);

  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [existingDenominationMap, setExistingDenominationMap] = useState<
    Map<string, DenominationInfo>
  >(new Map());
  const [processedExistingDenominationData, setProcessedExistingDenominationData] = useState<
    DenominationInfo[] | null
  >(null);
  const transferDefaults = useCallback(
    (providedDefaults?: FieldValues, force?: boolean) => {
      const defaultValues: FieldValues = {};
      const newFieldValues = structuredClone(fieldValues);
      const defaultsToUse = providedDefaults ?? defaultValues;

      for (const fieldID in defaultsToUse) {
        if (force || !Object.hasOwn(newFieldValues, fieldID)) {
          newFieldValues[fieldID] = defaultsToUse[fieldID];
        }
      }

      setFieldValues(newFieldValues);
    },
    [fieldValues],
  );
  const getBagType = (denominationType: string): DenominationBagType => {
    if (denominationType === 'useable_coins_part_bag') {
      return DenominationBagType.partBag;
    }
    if (denominationType === 'useable_coins_full_bag') {
      return DenominationBagType.fullBag;
    }
    return DenominationBagType.unknown;
  };

  const populateDenominationMaps = (
    denominations: DenominationInfo[],
    denominationMap: Map<string, DenominationInfo>,
    denominationMapWithPartBags: Map<string, DenominationInfo>,
  ) => {
    denominations.forEach((denomination) => {
      const { partBagCoins, itemID = '' } = denomination;
      denominationMap.set(itemID, denomination);
      denominationMapWithPartBags.set(itemID, denomination);

      if (partBagCoins) {
        denominationMapWithPartBags.set(partBagCoins.itemID ?? '', denomination);
      }
    });
  };

  const processExistingDenominations = (
    existingDenominationData: DenominationInfo[],
    denominationMapWithPartBags: Map<string, DenominationInfo>,
    bagType: DenominationBagType,
  ) => {
    const defaultValues: FieldValues = {};
    const denominationsToUse = new Set<DenominationInfo>();
    const existingDenominationMap = new Map<string, DenominationInfo>();
    let hasDefaultChangingValues = false;

    existingDenominationData?.forEach((prefilledDenomination) => {
      const { type, quantity, itemID, value } = prefilledDenomination;
      if (!itemID) return;

      const denomination = denominationMapWithPartBags.get(itemID);
      if (!denomination) {
        existingDenominationMap.set(itemID, prefilledDenomination);
        return;
      }

      const quantityFieldID = getFieldName(denomination, 'quantity');
      const looseCoinsID = getFieldName(denomination, 'looseCoins');
      const valueFieldID = getFieldName(denomination, 'value');

      const existingValue = defaultValues?.[valueFieldID] || 0;
      const newValue = +(value ?? 0);

      if (quantity !== undefined || value !== undefined) {
        hasDefaultChangingValues = true;
      }

      if (type === 'coin' && bagType === DenominationBagType.partBag) {
        defaultValues[looseCoinsID] = +(quantity ?? 0);
      } else {
        defaultValues[quantityFieldID] = +(quantity ?? 0);
      }
      defaultValues[valueFieldID] = newValue + existingValue;
      if (newValue) {
        denominationsToUse.add(denomination);
      }
      existingDenominationMap.set(itemID, prefilledDenomination);
    });

    return {
      defaultValues,
      hasDefaultChangingValues,
      denominationsToUse,
      existingDenominationMap,
    };
  };

  const getReadOnlyDenominations = (denominationsToUse: Set<DenominationInfo>) => {
    return Array.from(denominationsToUse).sort(
      (a, b) => Number(b.unitValuePence) - Number(a.unitValuePence),
    );
  };

  /* TODO: This hack needs to be removed when `CashBalance` is corrected */
  const shouldForceDefaults = (
    hasDefaultChangingValues: boolean,
    existingDenominationData: DenominationInfo[],
    processedExistingDenominationData: DenominationInfo[],
  ) => {
    return (
      hasDefaultChangingValues &&
      Array.isArray(existingDenominationData) &&
      processedExistingDenominationData === null
    );
  };

  const { denominations, denominationMap, bagType, defaultValues } = useMemo(() => {
    const denominations = denominationLadderForPouchSubType(denominationType as PouchSubType) || [];
    const denominationMap = new Map<string, DenominationInfo>();
    const denominationMapWithPartBags = new Map<string, DenominationInfo>();

    const bagType = getBagType(denominationType ?? '');

    populateDenominationMaps(denominations, denominationMap, denominationMapWithPartBags);

    const { defaultValues, hasDefaultChangingValues, denominationsToUse, existingDenominationMap } =
      processExistingDenominations(
        existingDenominationData ?? [],
        denominationMapWithPartBags,
        bagType,
      );

    const readOnlyDenominations = getReadOnlyDenominations(denominationsToUse);

    /* TODO: This hack needs to be removed when `CashBalance` is corrected */
    const forceDefaults = shouldForceDefaults(
      hasDefaultChangingValues,
      existingDenominationData ?? [],
      processedExistingDenominationData ?? [],
    );

    transferDefaults(defaultValues, forceDefaults);

    if (hasDefaultChangingValues) {
      setProcessedExistingDenominationData(existingDenominationData ?? null);
    }
    /* TODO: End CashBalance hack */

    return {
      bagType,
      denominations: readOnly ? readOnlyDenominations : denominations,
      denominationMap,
      defaultValues,
      existingDenominationMap,
    };
  }, [denominationType, existingDenominationData, readOnly]);

  const isPartBag = bagType === 'partBag';

  const detectQuantityOrValueMode = (
    dirtyFields: Set<string>,
    fieldType: string | null,
    itemID: string | null,
  ): FormMode | null => {
    let mode: FormMode | null = null;
    dirtyFields.forEach((field) => {
      [fieldType, itemID] = field.split('-');
      const denomination = denominationMap.get(itemID);
      if (denomination?.label?.includes('Non-Usable')) {
        return;
      }
      if (fieldType === 'looseCoins') {
        mode = 'quantity';
      }
      if (['quantity', 'value'].includes(fieldType)) {
        mode = fieldType as FormMode;
      }
    });
    return mode;
  };

  const determineExistingDenomDataMode = (
    mode: FormMode | null,
    existingDenominationData: DenominationInfo[],
    hasValue: boolean,
    itemHasManualValue: boolean,
    hasValidQuantity: boolean,
    fieldType: string | null,
  ) => {
    if (existingDenominationData) {
      if (!hasValue) {
        mode = null;
      } else if (!itemHasManualValue && hasValidQuantity) {
        mode = 'quantity';
      } else if (fieldType === 'quantity') {
        mode = 'quantity';
      } else if (fieldType === 'value' || itemHasManualValue) {
        mode = 'value';
      }
    }
    return mode;
  };

  const getFilteredDataInfo = (existingDenominationData: DenominationInfo[]) => {
    const filteredData =
      existingDenominationData?.filter(
        (denomination) => !denomination.label?.includes('Non-Usable'),
      ) || [];

    return {
      itemHasManualValue: filteredData.some((denomination) => denomination.hasManualValue),
      hasValidQuantity: filteredData.some(
        (denomination) => denomination.quantity && denomination.quantity > 0,
      ),
      hasValue: filteredData.some(
        (denomination) =>
          (denomination.value && denomination.value !== '0') ||
          denomination.value === '0' ||
          denomination.quantity,
      ),
    };
  };

  /*
      If part bag: disable value fields
      If we have dirty fields: disable opposite side
      If we don't have dirty fields but have pre-existing data: give preference over quantity, otherwise value.
   */
  const formMode = useMemo(() => {
    let mode: FormMode = null;
    const fieldType: string | null = null;
    const itemID: string | null = null;

    if (dirtyFields.size) {
      mode = detectQuantityOrValueMode(dirtyFields, fieldType, itemID);
    }

    if (existingDenominationData) {
      const { itemHasManualValue, hasValidQuantity, hasValue } =
        getFilteredDataInfo(existingDenominationData);
      mode = determineExistingDenomDataMode(
        mode,
        existingDenominationData,
        hasValue,
        itemHasManualValue,
        hasValidQuantity,
        fieldType,
      );
    }

    if (
      dirtyFields.size === 0 &&
      !existingDenominationData?.find((denomination) => denomination.quantity)
    ) {
      mode = null;
    }

    return mode;
  }, [dirtyFields, existingDenominationData, denominationMap, countStatus]);

  const handleFieldStateChange = (stateChanges: FieldValues) => {
    const newState = { ...fieldValues, ...stateChanges };
    setFieldValues(newState);
  };

  const dirtyField = (fieldID: string, remove?: boolean) => {
    const newState = new Set(dirtyFields);
    /*
      Sets in state can get expensive and this triggers
      each key down so this is just as a micro optimization
    */
    let stateChanged = false;
    const fieldExists = newState.has(fieldID);

    if (!remove) {
      newState.add(fieldID);
      stateChanged = !fieldExists;
    } else {
      newState.delete(fieldID);
      stateChanged = fieldExists;
    }
    if (stateChanged) setDirtyFields(newState);
  };

  // updates dirty fields in recount ladder using the previous ladders existingDenominationData
  useEffect(() => {
    if (countStatus === 'recount' && existingDenominationData) {
      const filteredDenominations = existingDenominationData.filter(
        (denomination) => !denomination.label?.includes('Non-Usable'),
      );

      const dirtyFieldIDs = filteredDenominations.flatMap(({ itemID, value, hasManualValue }) => {
        return (hasManualValue && value !== undefined) || !hasManualValue
          ? [`value-${itemID}`]
          : [`quantity-${itemID}`];
      });

      const updatedDirtyFields = new Set(dirtyFieldIDs);
      setDirtyFields(updatedDirtyFields);
    }
  }, [countStatus, existingDenominationData]);

  const checkDenominationError = useCallback(
    (updatedDenomination: DenominationInfo, actualValue: number) => {
      const unitValuePence = updatedDenomination.unitValuePence ?? 0;
      const { label } = updatedDenomination;

      if (actualValue === 0 || label === 'Non-Usable Coins') {
        return { message: '', hasError: false };
      }

      if (label === 'Non-Usable Notes') {
        if (`${actualValue}`.endsWith('00')) return { message: '', hasError: false };
        return { message: t('errorMessages.invalidValue'), hasError: true };
      }

      const isDivisible = unitValuePence > 0 && actualValue % unitValuePence === 0;

      if (!isDivisible) {
        return {
          message: hideErrorCode
            ? `${t('common:errors.BO013.message')}`
            : `${t('common:errors.BO013.message')} (${t('common:errors.BO013.errorCode')})`,
          hasError: true,
        };
      }
      return { message: '', hasError: false };
    },
    [t, hideErrorCode],
  );

  const totalValue = useMemo(() => {
    let total = 0;

    denominations?.forEach((denomination) => {
      const valueFieldID = getFieldName(denomination, 'value');
      const value = fieldValues?.[valueFieldID] ?? (defaultValues[valueFieldID] || 0);

      const errorInfo = checkDenominationError(denomination, value);
      if (!errorInfo.hasError) {
        total += value;
      }
    });
    return total;
  }, [denominations, fieldValues, defaultValues, checkDenominationError]);

  const handleDenominationChange = (
    denomination: DenominationInfo,
    values: { value?: number; quantity?: number; looseCoins?: number; fieldValue?: string },
  ) => {
    if (denominationMap.has(denomination?.itemID || '')) {
      const updatedData = new Map<string, DenominationInfo>(
        existingDenominationMap.size ? existingDenominationMap : denominationMap,
      );
      const existingDenomination = updatedData.get(denomination?.itemID ?? '');
      if (existingDenomination) {
        // merge existingDenomination with updates from denomination
        const mergedDenomination = { ...existingDenomination, ...denomination };
        const updatedDenomination = updateDenominationWithValues(
          mergedDenomination,
          fieldValues,
          values,
        );
        updatedData.set(denomination?.itemID ?? '', updatedDenomination);
        setExistingDenominationMap(updatedData);
        onChange?.(Array.from(updatedData.values()));
      }
    }
  };

  const validateInput = (inputString: string) => /^\d*$/.test(inputString);

  const handleChange = (newQuantity: string, fieldID: string, denomination: DenominationInfo) => {
    const stateUpdates: FieldValues = {};
    const isValidInput = validateInput(newQuantity);

    if (!isValidInput) return;

    const quantityValue = +newQuantity;
    const maxQuantityInBag = getMaxQuantityInBag(denomination);
    const isExceedingMax = !isPartBag && maxQuantityInBag && quantityValue > maxQuantityInBag;
    const adjustedQuantityValue = isExceedingMax ? maxQuantityInBag : quantityValue;

    if (formMode !== 'value') {
      const valueFieldID = getFieldName(denomination, 'value');
      const looseCoinsFieldID = getFieldName(denomination, 'looseCoins');
      let newAmountValue = getBagQuantityValue(adjustedQuantityValue, denomination);

      const looseCoinQuantity = fieldValues?.[looseCoinsFieldID] || 0;
      if (looseCoinQuantity) {
        newAmountValue += getBagLooseCoinsValue(looseCoinQuantity, denomination);
      }
      stateUpdates[valueFieldID] = newAmountValue;
      const updatedDenomination = { ...denomination, value: newAmountValue.toString() };
      const { hasError } = checkDenominationError(updatedDenomination, newAmountValue);
      updatedDenomination.error = hasError;
      handleDenominationChange(updatedDenomination, {
        quantity: adjustedQuantityValue,
        value: stateUpdates[valueFieldID] ?? undefined,
        fieldValue: newQuantity,
      });
      stateUpdates[fieldID] = adjustedQuantityValue;
      handleFieldStateChange(stateUpdates);
    }

    dirtyField(fieldID, newQuantity === '');
  };

  const renderQuantityField = (denomination: DenominationInfo) => {
    return (
      <QuantityField
        handleChange={handleChange}
        denomination={denomination}
        defaultValues={defaultValues}
        fieldValues={fieldValues}
        dirtyFields={dirtyFields}
        readOnly={readOnly}
        disabled={disabled}
        isPartBag={isPartBag}
        formMode={formMode}
      />
    );
  };

  const renderCoinQuantityField = (denomination: DenominationInfo) => {
    const fieldID = getFieldName(denomination, 'looseCoins');
    const defaultValue = defaultValues?.[fieldID] || 0;
    const actualValue =
      fieldValues?.[fieldID] !== undefined ? fieldValues?.[fieldID] : defaultValue;

    if (readOnly) return defaultValue === 0 ? '0' : removeNegativeSymbol(defaultValue);

    const handleChange = (newQuantity: string) => {
      const stateUpdates: FieldValues = {};
      const quantityValue = +newQuantity;
      const newAmountValue = undefined;

      if (!validateInput(newQuantity)) return;

      if (formMode !== 'value') {
        const valueFieldID = getFieldName(denomination, 'value');
        const quantityFieldID = getFieldName(denomination, 'quantity');

        let newAmountValue = getBagLooseCoinsValue(quantityValue, denomination);

        const bagQuantity = fieldValues?.[quantityFieldID] || 0;
        if (bagQuantity) {
          newAmountValue += getBagQuantityValue(bagQuantity, denomination);
        }
        stateUpdates[valueFieldID] = newAmountValue;
      }

      if (fieldValues?.[fieldID] !== quantityValue) {
        handleDenominationChange(denomination, {
          looseCoins: quantityValue,
          value: newAmountValue,
        });
        stateUpdates[fieldID] = quantityValue;
        handleFieldStateChange(stateUpdates);
      }

      dirtyField(fieldID, newQuantity === '');
    };

    return (
      <Input
        className={styles.input}
        name={fieldID}
        testId={fieldID}
        defaultValue={defaultValue || ''}
        value={actualValue || ''}
        provideValueOnChange
        onChange={handleChange}
        placeholder={t('quantity')}
        maskFunc={numericOnlyMask}
        selectAllOnFocus
        disabled={disabled || denomination.unitValuePence === null || formMode === 'value'}
      />
    );
  };

  const checkExceedsMaximumError = (totalValue: number) => {
    const exceedsMaximum = maxLimit ? totalValue > maxLimit : false;
    const message = `${t('common:errors.BO043.message', {
      limit: amountSignedPound(maxLimit ?? 0, 'auto'),
    })} (${t('common:errors.BO043.errorCode')})`;
    return !!maxLimit && exceedsMaximum ? message : undefined;
  };

  const renderValueField = (denomination: DenominationInfo) => {
    const fieldID = getFieldName(denomination, 'value');

    const defaultValue = defaultValues?.[fieldID] || 0;

    if (readOnly) return amountSignedPound(Number(defaultValue), 'never');

    const actualValue =
      fieldValues?.[fieldID] !== undefined ? fieldValues?.[fieldID] : defaultValue;

    const isDisabled =
      disabled ||
      isPartBag ||
      (denomination.label &&
        denomination.label !== 'Non-Usable Notes' &&
        denomination.label !== 'Non-Usable Coins' &&
        formMode === 'quantity') ||
      undefined;

    const handleChange = (newValue: number) => {
      const stateUpdates: FieldValues = {};
      let newQuantityValue = undefined;

      const updatedDenomination = { ...denomination, value: newValue.toString() };
      updatedDenomination.hasManualValue = newValue !== 0;

      if (bagType !== 'partBag' && formMode !== 'quantity') {
        const quantityFieldID = getFieldName(denomination, 'quantity');

        const unitValue = updatedDenomination.unitValuePence ?? 0;
        const isDivisible = newValue % unitValue === 0;
        newQuantityValue = isDivisible ? newValue / unitValue : 0;
        stateUpdates[quantityFieldID] = newQuantityValue;
      }

      const { hasError } = checkDenominationError(updatedDenomination, newValue);
      updatedDenomination.error = hasError;

      onErrorChange?.(updatedDenomination.itemID ?? '', hasError);

      if (fieldValues?.[fieldID] !== newValue) {
        handleDenominationChange(updatedDenomination, {
          quantity: newQuantityValue,
          value: newValue,
        });
        stateUpdates[fieldID] = newValue;
        handleFieldStateChange(stateUpdates);
      }
      dirtyField(fieldID, !newValue);
    };

    const { message } = checkDenominationError(denomination, actualValue);

    return (
      <CurrencyInput
        name={fieldID}
        testId={fieldID}
        defaultValue={+defaultValue || 0}
        value={actualValue || 0}
        errorMessage={message}
        onChange={handleChange}
        disabled={isDisabled}
      />
    );
  };

  const resetForm = useCallback(() => {
    setFieldValues({});
    setDirtyFields(new Set());
    setExistingDenominationMap(new Map());
    setProcessedExistingDenominationData(null);
  }, []);

  /*
    To reduce the need for `onChange` and unnecessary re-renders a consumer can get the data from the ref instead
   */
  const getDenominationData = useCallback<() => DenominationInfo[]>(() => {
    const returnData = new Set<DenominationInfo>();
    denominations.forEach((denomination) => {
      returnData.add(updateDenominationWithValues(denomination, fieldValues, {}));
    });
    return Array.from(returnData);
  }, [denominations, fieldValues]);

  // check if default values need to be updated to reduce renders within the useEffect
  const shouldUpdateDefaults = (currentValues: object, defaultValues: FieldValues) => {
    return Object.keys(defaultValues).some((key) => !Object.hasOwn(currentValues, key));
  };

  useEffect(() => {
    if (defaultValues && shouldUpdateDefaults(fieldValues, defaultValues)) {
      transferDefaults();
    }
  }, [defaultValues, fieldValues, transferDefaults]);

  useImperativeHandle(
    ref,
    () => ({
      reset: resetForm,
      getData: getDenominationData,
    }),
    [getDenominationData, resetForm],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const targetElement = event.target as HTMLInputElement;
    if (event.key === 'Enter' && targetElement && !targetElement.disabled) {
      const inputElementNames = [
        'input[name*="quantity-"]',
        'input[name*="looseCoins-"]',
        'input[name*="value-"]',
      ];
      const inputElements = Array.from(
        document.querySelectorAll<HTMLInputElement>(inputElementNames.join()),
      ).filter((input) => !input.disabled);

      const currentIndex = inputElements.indexOf(targetElement);
      const nextInput = inputElements[(currentIndex + 1) % inputElements.length];
      event.preventDefault();
      nextInput.focus();
      nextInput.select();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <table className={classnames(styles.table, { [styles.readonlyTable]: readOnly })}>
        <DenominationLadderTableHeader bagType={bagType} readOnly={readOnly} />
        <tbody>
          {denominations.map((denomination) => (
            <tr key={denomination.itemID} className={styles.row}>
              <td className={styles.col}>{denomination.label}</td>
              {!readOnly && <td className={styles.col}>X</td>}
              <td className={styles.col}>{renderQuantityField(denomination)}</td>
              {isPartBag && <td className={styles.col}>{renderCoinQuantityField(denomination)}</td>}
              {!readOnly && <td className={styles.col}>=</td>}
              <td className={styles.col}>{renderValueField(denomination)}</td>
            </tr>
          ))}
          <tr className={styles.row}>
            <td className={styles.col}>{t('total')}</td>
            {maxLimit ? (
              <td colSpan={3} className={styles.warningCol}>
                <div className={styles.warningText}>{checkExceedsMaximumError(totalValue)}</div>
              </td>
            ) : (
              <>
                {!readOnly && <td className={styles.col}></td>}
                {isPartBag && <td className={styles.col}></td>}
                <td className={styles.col}></td>
                {!readOnly && <td className={styles.col}></td>}
              </>
            )}
            <td className={styles.col}>
              {readOnly ? (
                amountSignedPound(totalValue, 'never')
              ) : (
                <CurrencyInput
                  name="total-value"
                  testId="total-value"
                  value={totalValue}
                  invalidTotal={!!checkExceedsMaximumError(totalValue)}
                  disabled
                />
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const reffed = forwardRef(DenominationLadder);

export { reffed as DenominationLadder };
